import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import Navigation from '../components/Navigation';
import MarketCharts from '../components/MarketCharts';

export default function HomePage() {
  const [stats, setStats] = useState({
    clientCount: 0,
    assetsManaged: 0,
    yearsExperience: 0,
    successRate: 0,
  });

  // Animation for statistics
  useEffect(() => {
    const interval = setInterval(() => {
      setStats(prevStats => ({
        clientCount: prevStats.clientCount < 3500 ? prevStats.clientCount + 35 : 3500,
        assetsManaged: prevStats.assetsManaged < 100 ? prevStats.assetsManaged + 1 : 100,
        yearsExperience: prevStats.yearsExperience < 25 ? prevStats.yearsExperience + 0.25 : 25,
        successRate: prevStats.successRate < 97 ? prevStats.successRate + 0.97 : 97,
      }));
    }, 30);

    return () => clearInterval(interval);
  }, []);

  // Investment plans data
  const investmentPlans = [
    {
      name: "Standard Plan",
      description: "Perfect for beginners looking to start their investment journey with proven strategies.",
      price: "$1,000",
      features: [
        "10x Profit in 21 days",
        "2% Welcome bonus",
        "$1,000 earn $10,000",
        "$2,000 earn $20,000",
        "$3,000 earn $30,000",
        "$4,000 earn $40,000",
        "$5,000 earn $50,000"
      ],
      duration: "21 days",
      type: "Basic portfolio",
      support: "24/7 Email Support"
    },
    {
      name: "Premium Plan",
      description: "Enhanced investment strategy with higher returns and priority support.",
      price: "$6,000",
      features: [
        "15x Profit in 21 days",
        "3% Welcome bonus",
        "$6,000 earn $90,000",
        "$7,000 earn $105,000",
        "$8,000 earn $120,000",
        "$9,000 earn $135,000",
        "$10,000 earn $150,000"
      ],
      duration: "21 days",
      type: "Enhanced portfolio",
      support: "24/7 Priority Chat Support",
      popular: true
    },
    {
      name: "Advanced Plan",
      description: "VIP investment package with maximum returns and personalized management.",
      price: "$10,000",
      features: [
        "20x Profit in 21 days",
        "5% Welcome bonus",
        "$10,000 earn $200,000",
        "$11,000 earn $220,000",
        "$12,000 earn $240,000",
        "$13,000 earn $260,000",
        "$14,000 earn $280,000",
        "VIP Account Manager",
        "Priority Withdrawals",
        "Exclusive Market Analysis"
      ],
      duration: "21 days",
      type: "VIP Wealth Management",
      support: "Dedicated Account Manager"
    },
    {
      name: "Platinum Plan",
      description: "Elite investment package with exceptional returns and premium services.",
      price: "$25,000",
      features: [
        "25x Profit in 21 days",
        "7% Welcome bonus",
        "$25,000 earn $625,000",
        "$30,000 earn $750,000",
        "$35,000 earn $875,000",
        "$40,000 earn $1,000,000",
        "$45,000 earn $1,125,000",
        "Elite Account Manager",
        "Instant Withdrawals",
        "Advanced Market Analysis",
        "Private Investment Events",
        "Personalized Portfolio Strategy"
      ],
      duration: "21 days",
      type: "Elite Wealth Management",
      support: "24/7 Personal Concierge"
    },
    {
      name: "Diamond Plan",
      description: "Executive-level investment package with maximum returns and exclusive benefits.",
      price: "$50,000",
      features: [
        "30x Profit in 21 days",
        "10% Welcome bonus",
        "$50,000 earn $1,500,000",
        "$60,000 earn $1,800,000",
        "$70,000 earn $2,100,000",
        "$80,000 earn $2,400,000",
        "$90,000 earn $2,700,000",
        "Executive Account Manager",
        "Instant Priority Withdrawals",
        "Expert Market Analysis",
        "Exclusive Investment Events",
        "Custom Portfolio Strategy",
        "Global Investment Opportunities",
        "Wealth Preservation Consulting"
      ],
      duration: "21 days",
      type: "Executive Wealth Management",
      support: "Dedicated Executive Team"
    }
  ];

  // Testimonials data
  const testimonials = [
    {
      quote: "WealthQuota has transformed my financial future. Their personalized approach and expertise helped me exceed my retirement goals.",
      author: "Sarah Johnson",
      position: "Retired Executive"
    },
    {
      quote: "As a business owner, I needed an investment partner who understood my unique needs. WealthQuota delivered beyond my expectations.",
      author: "Michael Chen",
      position: "CEO, Tech Solutions Inc."
    },
    {
      quote: "Their strategic investment advice has been crucial to growing my portfolio even during market downturns.",
      author: "David Rodriguez",
      position: "Finance Professional"
    }
  ];

  // Custom icons to replace lucide-react
  const Icons = {
    ArrowRight: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m5 12h14"></path>
        <path d="m12 5 7 7-7 7"></path>
      </svg>
    ),
    TrendingUp: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
        <polyline points="16 7 22 7 22 13"></polyline>
      </svg>
    ),
    ChartBar: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"></line>
        <line x1="12" y1="20" x2="12" y2="4"></line>
        <line x1="6" y1="20" x2="6" y2="14"></line>
        <line x1="2" y1="20" x2="22" y2="20"></line>
      </svg>
    ),
    Shield: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
      </svg>
    ),
    Target: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <circle cx="12" cy="12" r="6"></circle>
        <circle cx="12" cy="12" r="2"></circle>
      </svg>
    ),
    Briefcase: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
      </svg>
    ),
    Users: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
      </svg>
    ),
    Award: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="7"></circle>
        <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
      </svg>
    ),
    Quote: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"></path>
        <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"></path>
      </svg>
    )
  };

  return (
    <div className="min-h-screen bg-background-light">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-primary-500 text-white">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-50"
          style={{ backgroundImage: 'url("/images/hero-bg.jpg")' }}
        ></div>
        <div className="absolute inset-0 bg-primary-900 opacity-60"></div>
        <div className="container mx-auto px-6 py-16 md:py-24 relative z-10">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-12 md:mb-0 text-center md:text-left">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Secure Your Financial Future with Expert Investment Solutions
              </h1>
              <p className="text-base sm:text-lg md:text-xl mb-8 text-primary-100 mx-auto md:mx-0 max-w-xl">
                WealthQuota Investment Group offers personalized investment strategies designed to grow and protect your wealth for generations to come.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <a 
                  href="/investment-plans" 
                  className="bg-secondary-500 hover:bg-secondary-600 text-white px-8 py-3 rounded-md font-medium transition-colors flex items-center justify-center"
                >
                  Explore Investment Plans <span className="ml-2"><Icons.ArrowRight /></span>
                </a>
                <a 
                  href="/contact" 
                  className="bg-transparent border-2 border-white hover:bg-white hover:text-primary-500 text-white px-8 py-3 rounded-md font-medium transition-colors flex items-center justify-center"
                >
                  Schedule Consultation
                </a>
              </div>
            </div>
            <div className="md:w-1/2 md:pl-12 w-full max-w-lg mx-auto">
              <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8 text-primary-800">
                <h3 className="text-2xl font-semibold mb-6 text-primary-600">Calculate Your Returns</h3>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-neutral-600 mb-2">Select Investment Plan</label>
                    <select 
                      onChange={(e) => {
                        const plan = investmentPlans.find(p => p.name === e.target.value);
                        const input = document.getElementById('investment-amount');
                        if (plan) {
                          input.min = plan.price.replace('$', '').replace(',', '');
                          input.placeholder = `Min. ${plan.price}`;
                        }
                      }}
                      className="w-full px-4 py-3 rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 bg-white"
                    >
                      <option value="">Choose a plan</option>
                      {investmentPlans.map((plan, index) => (
                        <option key={index} value={plan.name}>
                          {plan.name} (Min. {plan.price})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-neutral-600 mb-2">Investment Amount ($)</label>
                    <input 
                      type="number"
                      id="investment-amount"
                      placeholder="Enter amount"
                      className="w-full px-4 py-3 rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-secondary-500"
                      onChange={(e) => {
                        const amount = parseFloat(e.target.value);
                        const planSelect = e.target.parentElement.parentElement.querySelector('select');
                        const plan = investmentPlans.find(p => p.name === planSelect.value);
                        
                        if (plan && amount) {
                          const minInvestment = parseFloat(plan.price.replace('$', '').replace(',', ''));
                          let profitMultiplier;
                          let welcomeBonus;
                          
                          switch(plan.name) {
                            case 'Standard Plan':
                              profitMultiplier = 10;
                              welcomeBonus = 0.02;
                              break;
                            case 'Premium Plan':
                              profitMultiplier = 15;
                              welcomeBonus = 0.03;
                              break;
                            case 'Advanced Plan':
                              profitMultiplier = 20;
                              welcomeBonus = 0.05;
                              break;
                            case 'Platinum Plan':
                              profitMultiplier = 25;
                              welcomeBonus = 0.07;
                              break;
                            case 'Diamond Plan':
                              profitMultiplier = 30;
                              welcomeBonus = 0.10;
                              break;
                            default:
                              profitMultiplier = 10;
                              welcomeBonus = 0.02;
                          }
                          
                          const returnAmount = amount * profitMultiplier;
                          const welcomeBonusAmount = amount * welcomeBonus;
                          const totalReturn = returnAmount + welcomeBonusAmount;
                          
                          document.getElementById('return-amount').textContent = `$${totalReturn.toLocaleString()}`;
                          document.getElementById('return-duration').textContent = plan.duration;
                          document.getElementById('welcome-bonus').textContent = `$${welcomeBonusAmount.toLocaleString()}`;
                          document.getElementById('calculator-results').classList.remove('hidden');
                        }
                      }}
                    />
                  </div>
                  
                  <div id="calculator-results" className="hidden">
                    <div className="bg-primary-50 rounded-lg p-4 space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-neutral-600">Total Return:</span>
                        <span className="text-2xl font-bold text-primary-600" id="return-amount">$0</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-neutral-600">Duration:</span>
                        <span className="font-medium text-primary-700" id="return-duration">0 days</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-neutral-600">Welcome Bonus:</span>
                        <span className="font-medium text-primary-700" id="welcome-bonus">$0</span>
                      </div>
                  </div>
                  </div>
                  
                  <a 
                    href="/investment-plans" 
                    className="block w-full bg-secondary-500 hover:bg-secondary-600 text-white py-3 rounded-md font-medium transition-colors text-center"
                  >
                    Start Investing Now
                  </a>
                  
                  <p className="text-xs text-center text-neutral-500">
                    Calculate your potential returns based on our investment plans. 
                    Results shown include welcome bonus.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-background-paper">
        <div className="container mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-primary-800 mb-4 text-center">Live Market Overview</h2>
            <MarketCharts />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary-500 mb-2">{stats.clientCount.toLocaleString()}+</div>
              <div className="text-sm sm:text-base text-neutral-600">Satisfied Clients</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary-500 mb-2">
                <span className="text-2xl sm:text-3xl md:text-4xl">$</span>
                {stats.assetsManaged.toFixed(1)}
                <span className="text-2xl sm:text-3xl md:text-4xl">M+</span>
              </div>
              <div className="text-sm sm:text-base text-neutral-600">Assets Managed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary-500 mb-2">{stats.yearsExperience}+</div>
              <div className="text-sm sm:text-base text-neutral-600">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary-500 mb-2">{stats.successRate.toFixed(0)}%</div>
              <div className="text-sm sm:text-base text-neutral-600">Success Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 md:pr-12 mb-12 md:mb-0">
              <div className="relative">
                <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden shadow-xl">
                  <img 
                    src="/images/head.jpg" 
                    alt="WealthQuota Investment Team" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute bottom-0 right-0 md:-bottom-6 md:-right-6 bg-secondary-500 text-white p-4 md:p-6 rounded-lg shadow-lg transform translate-y-1/2 md:translate-y-0">
                  <p className="font-bold text-lg md:text-xl">25+ Years</p>
                  <p className="text-xs md:text-sm">of Financial Excellence</p>
                </div>
              </div>
            </div>
            <div className="md:w-1/2">
              <h2 className="text-3xl md:text-4xl font-bold text-primary-800 mb-6">About WealthQuota Investment Group</h2>
              <p className="text-neutral-700 mb-6">
                Founded on principles of integrity, expertise, and personalized service, WealthQuota Investment Group has been a trusted partner in wealth management for over two decades. Our team of seasoned financial advisors brings unparalleled knowledge and strategic insight to every client relationship.
              </p>
              <p className="text-neutral-700 mb-8">
                We believe in a holistic approach to wealth management, considering your complete financial picture to create investment strategies aligned with your goals, risk tolerance, and timeline.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
                <div className="flex items-start space-x-4 p-4 rounded-lg hover:bg-neutral-50 transition-colors">
                  <div className="bg-primary-50 p-3 rounded-full shrink-0">
                    <span className="text-primary-500"><Icons.Target /></span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary-800 text-lg mb-2">Goal-Oriented</h4>
                    <p className="text-sm text-neutral-600 leading-relaxed">Strategies aligned with your unique financial objectives</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-4 rounded-lg hover:bg-neutral-50 transition-colors">
                  <div className="bg-primary-50 p-3 rounded-full shrink-0">
                    <span className="text-primary-500"><Icons.Briefcase /></span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary-800 text-lg mb-2">Professional Team</h4>
                    <p className="text-sm text-neutral-600 leading-relaxed">Certified advisors with decades of experience</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-4 rounded-lg hover:bg-neutral-50 transition-colors">
                  <div className="bg-primary-50 p-3 rounded-full shrink-0">
                    <span className="text-primary-500"><Icons.Shield /></span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary-800 text-lg mb-2">Risk Management</h4>
                    <p className="text-sm text-neutral-600 leading-relaxed">Comprehensive approach to protect your investments</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-4 rounded-lg hover:bg-neutral-50 transition-colors">
                  <div className="bg-primary-50 p-3 rounded-full shrink-0">
                    <span className="text-primary-500"><Icons.Users /></span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary-800 text-lg mb-2">Personalized Service</h4>
                    <p className="text-sm text-neutral-600 leading-relaxed">Tailored solutions based on your unique situation</p>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <a 
                  href="/about" 
                  className="inline-flex items-center text-secondary-500 hover:text-secondary-600 font-medium"
                >
                  Learn more about our approach <span className="ml-2"><Icons.ArrowRight /></span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

{/* Certificates & Licenses Section */}
<section className="py-16 bg-neutral-50">
  <div className="container mx-auto px-6">
    <div className="text-center mb-12">
      <h2 className="text-3xl md:text-4xl font-bold text-primary-800 mb-4">Our Certificates & Licenses</h2>
      <p className="text-neutral-600 max-w-2xl mx-auto">
        We maintain the highest standards of regulatory compliance and professional certification in the investment industry.
      </p>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
      <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow flex flex-col">
        <div className="mb-4 text-center">
          <h3 className="text-xl font-semibold text-primary-800">Insurance Certificate</h3>
          <p className="text-sm text-neutral-600 mt-1">Werla Insurance Company Limited</p>
        </div>
        <div className="rounded-lg overflow-hidden flex-grow">
          <img 
            src="/images/cert1.jpeg" 
            alt="WealthQuota Insurance Certificate" 
            className="w-full h-full object-contain"
          />
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow flex flex-col">
        <div className="mb-4 text-center">
          <h3 className="text-xl font-semibold text-primary-800">Investment License</h3>
          <p className="text-sm text-neutral-600 mt-1">Financial Services Authority</p>
        </div>
        <div className="rounded-lg overflow-hidden flex-grow">
          <img 
            src="/images/cert2.jpeg" 
            alt="WealthQuota Investment License" 
            className="w-full h-full object-contain"
          />
        </div>
      </div>
    </div>

    <div className="text-center mt-8">
      <p className="text-sm text-neutral-500">
        Registration Number: I 90 763 781 563 • Fully Licensed and Insured
      </p>
          </div>
        </div>
      </section>

      {/* Investment Plans Section */}
      <section className="py-20 bg-neutral-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-800 mb-4">Investment Solutions Tailored For You</h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              Discover our range of investment options designed to meet your financial goals with guaranteed returns.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {investmentPlans.slice(0, 3).map((plan, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-xl shadow-lg overflow-hidden transition-transform duration-300 hover:-translate-y-2 relative ${
                  plan.popular ? 'ring-2 ring-primary-500' : ''
                }`}
              >
                {plan.popular && (
                  <div className="bg-primary-500 text-white px-4 py-1 text-sm font-medium absolute top-0 right-0 z-10">
                    Most Popular
                  </div>
                )}
                <div className="p-8">
                  <div className="mb-6 text-secondary-500">
                    {index === 0 ? <Icons.TrendingUp /> : 
                     index === 1 ? <Icons.ChartBar /> : <Icons.Shield />}
                  </div>
                  <h3 className="text-2xl font-bold text-primary-800 mb-4">{plan.name}</h3>
                  <div className="flex items-baseline mb-4">
                    <span className="text-3xl font-bold text-primary-600">{plan.price}</span>
                    <span className="ml-2 text-neutral-500">initial</span>
                  </div>
                  <p className="text-neutral-600 mb-6">{plan.description}</p>
                  <div className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start text-sm">
                        <span className="text-primary-500 mr-2">✓</span>
                        <span className="text-neutral-600">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-3 mb-8">
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-500">Duration:</span>
                      <span className="font-medium text-primary-700">{plan.duration}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-500">Type:</span>
                      <span className="font-medium text-primary-700">{plan.type}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-500">Support:</span>
                      <span className="font-medium text-primary-700">{plan.support}</span>
                    </div>
                  </div>
                  <a 
                    href="/contact"
                    className={`block w-full text-center py-3 rounded-md font-medium transition-colors ${
                      plan.popular
                        ? 'bg-primary-500 hover:bg-primary-600 text-white'
                        : 'bg-neutral-100 hover:bg-neutral-200 text-primary-800'
                    }`}
                  >
                    Get Started
                  </a>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <a 
              href="/investment-plans" 
              className="inline-flex items-center bg-secondary-500 hover:bg-secondary-600 text-white px-8 py-3 rounded-md font-medium transition-colors"
            >
              View All Investment Plans <span className="ml-2"><Icons.ArrowRight /></span>
            </a>
            <p className="text-sm text-neutral-600 mt-4">
              Discover our premium Platinum and Diamond plans with enhanced benefits and higher returns
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-500 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600 to-primary-400 opacity-50"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Secure Your Financial Future?</h2>
            <p className="text-lg mb-8 text-primary-100">
              Join thousands of satisfied clients who have trusted WealthQuota Investment Group with their financial goals. Our team of experts is ready to create a personalized investment strategy for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/signup" 
                className="bg-secondary-500 hover:bg-secondary-600 text-white px-8 py-3 rounded-md font-medium transition-colors"
              >
                Get Started Today
              </a>
              <a 
                href="/contact" 
                className="bg-transparent border-2 border-white hover:bg-white hover:text-primary-500 text-white px-8 py-3 rounded-md font-medium transition-colors"
              >
                Speak with an Advisor
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-800 mb-4">What Our Clients Say</h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              Discover why thousands of investors trust WealthQuota Investment Group with their financial future.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-neutral-50 p-8 rounded-xl relative">
                <div className="absolute top-0 left-8 transform -translate-y-1/2 bg-secondary-500 text-white w-12 h-12 rounded-full flex items-center justify-center">
                  <Icons.Quote />
                </div>
                <div className="pt-8">
                  <p className="text-neutral-700 mb-6">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-semibold text-primary-800">{testimonial.author}</p>
                    <p className="text-sm text-neutral-500">{testimonial.position}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-primary-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-800 mb-4">Why Choose WealthQuota</h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              We combine industry expertise, personalized service, and innovative strategies to help you achieve your financial goals.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="bg-primary-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white"><Icons.Award /></span>
              </div>
              <h3 className="text-xl font-bold text-primary-800 mb-3">Award-Winning Services</h3>
              <p className="text-neutral-600">
                Recognized for excellence in investment management and client satisfaction.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="bg-primary-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white"><Icons.ChartBar /></span>
              </div>
              <h3 className="text-xl font-bold text-primary-800 mb-3">Data-Driven Strategies</h3>
              <p className="text-neutral-600">
                Investment decisions backed by rigorous research and market analysis.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="bg-primary-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white"><Icons.Shield /></span>
              </div>
              <h3 className="text-xl font-bold text-primary-800 mb-3">Security & Compliance</h3>
              <p className="text-neutral-600">
                Adhering to the highest standards of regulatory compliance and security.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="bg-primary-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white"><Icons.Users /></span>
              </div>
              <h3 className="text-xl font-bold text-primary-800 mb-3">Dedicated Advisors</h3>
              <p className="text-neutral-600">
                Personal attention from experienced financial professionals committed to your success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-white border-t border-neutral-200">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-primary-800 mb-4">Stay Informed with Market Insights</h3>
            <p className="text-neutral-600 mb-8">
              Subscribe to our newsletter for expert investment insights, market trends, and financial tips.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <input 
                type="email" 
                placeholder="Your Email Address" 
                className="flex-grow px-4 py-3 rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-secondary-500"
              />
              <button 
                className="bg-secondary-500 hover:bg-secondary-600 text-white px-6 py-3 rounded-md font-medium transition-colors whitespace-nowrap"
              >
                Subscribe
              </button>
            </div>
            <p className="text-xs text-neutral-500 mt-4">
              We respect your privacy. Unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>

      {/* Pre-Footer CTA */}
      <section className="bg-accent-500 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-6 md:mb-0">
              <h3 className="text-2xl font-bold">Ready to start your investment journey?</h3>
              <p className="text-accent-100">Create your account today and take the first step toward financial freedom.</p>
            </div>
            <div>
              <a 
                href="/signup" 
                className="bg-white text-accent-500 hover:bg-neutral-100 px-8 py-3 rounded-md font-medium transition-colors inline-block"
              >
                Sign Up Now
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}